import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllClaims, updateClaimStatus, authorizePayment, selectClaims, selectClaimsLoading, selectClaimsError } from '../../features/claims/claimSlice';
import { useToast } from '../../contexts/ToastContext';
import LoadingState from '../ui/LoadingState';
import ErrorMessage from '../ClaimsList/ErrorMessage';
import OmButton from '../ui/OmButton';
import OmPagination from '../ui/OmPagination';
import OmCircularProgress from '../ui/OmCircularProgress';
import DashboardLayout from '../layout/DashboardLayout';
import DashboardSection, { CardSection, QuickActionsSection } from '../layout/DashboardSection';
import StatisticCard from '../dashboard/StatisticsComponents/StatisticCard';
import { useDashboardStatistics } from '../../hooks/useDashboardStats';
import bankService from '../../services/bankService';

const PaymentManagement = () => {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();
  const claims = useSelector(selectClaims);
  const loading = useSelector(selectClaimsLoading);
  const error = useSelector(selectClaimsError);
  const [filteredClaims, setFilteredClaims] = useState([]);
  const [processingPayments, setProcessingPayments] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [paginatedClaims, setPaginatedClaims] = useState([]);
  const [branchNames, setBranchNames] = useState({}); // Map of branchId -> branchName
  const { statistics, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = useDashboardStatistics();

  // Load bank branch data for decoding branch codes
  useEffect(() => {
    const loadBranchNames = async () => {
      try {
        const options = await bankService.getBankBranchOptions();
        const branchMap = {};
        options.forEach(option => {
          if (option.branchId) {
            branchMap[option.branchId] = option.branchName || option.label;
          }
        });
        setBranchNames(branchMap);
      } catch (error) {
        console.error('Failed to load branch names:', error);
      }
    };
    loadBranchNames();
  }, []);

  // Helper function to get branch name from branch code
  const getBranchName = (branchCode) => {
    if (!branchCode) return null;
    return branchNames[branchCode] || `Branch ${branchCode}`;
  };

  // Load claims on component mount
  useEffect(() => {
    dispatch(getAllClaims());
  }, [dispatch]);

  // Filter claims for payment processing (APPROVED or PAYMENT_PENDING)
  useEffect(() => {
    if (!claims || !Array.isArray(claims)) {
      setFilteredClaims([]);
      return;
    }
    
    const paymentClaims = claims.filter(claim => 
      claim.status === 'APPROVED' || claim.status === 'PAYMENT_PENDING'
    );
    setFilteredClaims(paymentClaims);
  }, [claims]);

  // Handle pagination
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedClaims(filteredClaims.slice(startIndex, endIndex));
  }, [filteredClaims, currentPage, itemsPerPage]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Calculate summary statistics (prefer backend statistics when available)
  const backendCounts = statistics?.counts || {};
  const summaryStats = {
    totalClaims: Number(backendCounts.total) || Number(backendCounts.all) || filteredClaims.length,
    approvedClaims: Number(backendCounts.approved) || filteredClaims.filter(c => c.status === 'APPROVED').length,
    pendingPayment: filteredClaims.filter(c => c.status === 'PAYMENT_PENDING').length,
    totalAmount: filteredClaims.reduce((sum, claim) => sum + (parseFloat(claim.approvedBenefitAmount || claim.benefitAmount) || 0), 0)
  };

  const handleStatusUpdate = async (claimId, newStatus) => {
    if (processingPayments.has(claimId)) return;

    setProcessingPayments(prev => new Set(prev).add(claimId));
    
    try {
      const result = await dispatch(updateClaimStatus({ 
        claimId, 
        status: newStatus,
        comment: `Payment status updated to ${newStatus} by Finance Officer`
      })).unwrap();
      
      // Show appropriate message based on whether claim was already in the status
      if (result?.alreadyInStatus) {
        showSuccess(`Claim is already in ${newStatus} status`);
      } else {
        showSuccess(`Claim status updated to ${newStatus}`);
      }
      
      // Force refresh claims list and backend statistics
      await dispatch(getAllClaims());
      await refetchStats();
      
      // Reset to first page to avoid pagination issues after status updates
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to update claim status:', error);
      showError(error?.message || 'Failed to update claim status');
    } finally {
      setProcessingPayments(prev => {
        const newSet = new Set(prev);
        newSet.delete(claimId);
        return newSet;
      });
    }
  };

  const handleAuthorizePayment = async (claimId) => {
    if (processingPayments.has(claimId)) return;

    setProcessingPayments(prev => new Set(prev).add(claimId));
    
    try {
      const result = await dispatch(authorizePayment({ 
        claimId, 
        authorizationNotes: 'Payment authorized and processed by Finance Officer',
        paymentMethod: 'BANK_TRANSFER'
      })).unwrap();
      
      showSuccess(result?.message || 'Payment authorized successfully');
      
      // Force refresh claims list and backend statistics
      await dispatch(getAllClaims());
      await refetchStats();
      
      // Reset to first page to avoid pagination issues after status updates
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to authorize payment:', error);
      showError(error?.message || 'Failed to authorize payment');
    } finally {
      setProcessingPayments(prev => {
        const newSet = new Set(prev);
        newSet.delete(claimId);
        return newSet;
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS'
    }).format(amount || 0);
  };

  const formatDate = (dateLike) => {
    if (!dateLike) return '—';
    const date = typeof dateLike === 'string' || typeof dateLike === 'number' ? new Date(dateLike) : dateLike;
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GH');
  };

  const getSubmittedDate = (claim) => {
    // Prefer backend canonical fields, then fall back to legacy names
    return claim?.submittedAt || claim?.createdAt || claim?.submissionDate || null;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'APPROVED': 'bg-green-100 text-green-800',
      'PAYMENT_PENDING': 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if ((loading || statsLoading) && (!claims || claims.length === 0)) {
    return <LoadingState message="Loading payment claims..." />;
  }

  if ((error || statsError) && (!claims || claims.length === 0)) {
    return <ErrorMessage isError={true} message="Failed to load claims for payment processing" />;
  }

  return (
    <DashboardLayout>
      <DashboardSection
        title="Payment Management"
        subtitle="Process payments for approved claims and manage payment status"
        spacing="tight"
      >
      </DashboardSection>

      {/* Statistics Section */}
      <DashboardSection
        title="Payment Statistics"
        subtitle="Overview of claims ready for payment processing"
        spacing="default"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatisticCard
            title="Total Claims"
            value={summaryStats.totalClaims}
            icon="claims"
          />
          <StatisticCard
            title="Ready for Payment"
            value={summaryStats.approvedClaims}
            icon="approved"
          />
          <StatisticCard
            title="Payment Pending"
            value={summaryStats.pendingPayment}
            icon="pending"
          />
          <StatisticCard
            title="Total Amount"
            value={formatCurrency(summaryStats.totalAmount)}
            icon="server"
          />
        </div>
      </DashboardSection>

      {/* Quick Actions Section */}
      <QuickActionsSection
        title="Payment Processing"
        subtitle="Process payments and manage claim statuses"
        spacing="default"
      >

        {/* Claims Table */}
        <CardSection>
          <div className="px-1 py-1">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Claims for Payment Processing</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Claims that are approved and ready for payment processing
            </p>
          </div>
          {paginatedClaims.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">{filteredClaims.length === 0 ? 'No claims ready for payment' : 'No claims on this page'}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filteredClaims.length === 0 ? 'Claims will appear here when they are approved and ready for payment processing.' : 'Try navigating to a different page or adjusting your filters.'}
                </p>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {paginatedClaims.map((claim) => (
                <li key={claim.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {claim.claimType?.charAt(0) || 'C'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">
                            {claim.claimNumber}
                          </p>
                          <div className="ml-2">
                            {getStatusBadge(claim.status)}
                          </div>
                        </div>
                        <div className="mt-1">
                          <p className="text-sm text-gray-600">
                            {claim.claimantName} • {claim.claimType}
                          </p>
                          <p className="text-sm text-gray-500">
                            Submitted: {formatDate(getSubmittedDate(claim))}
                          </p>
                          
                          {/* Enhanced Payment & Contact Details */}
                          {(claim.bankName || claim.accountNumber || claim.preferredPaymentMethod || claim.claimantMobile) && (
                            <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
                              <div className="flex items-center mb-3">
                                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                                <span className="text-sm font-semibold text-blue-800 uppercase tracking-wide">
                                  Payment & Contact Information
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Payment Details Column */}
                                {(claim.bankName || claim.accountNumber || claim.preferredPaymentMethod) && (
                                  <div className="space-y-2">
                                    <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center border-b border-blue-200 pb-1">
                                      <svg className="w-4 h-4 mr-1.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                      </svg>
                                      Payment Details
                                    </p>
                                    
                                    {claim.preferredPaymentMethod && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-24 flex-shrink-0 font-medium">Method:</span>
                                        <span className="text-sm font-semibold text-gray-900">
                                          {claim.preferredPaymentMethod === 'MOBILE_MONEY' ? 'Mobile Money' : 
                                           claim.preferredPaymentMethod === 'BANK_TRANSFER' ? 'Bank Transfer' : 
                                           claim.preferredPaymentMethod}
                                        </span>
                                      </div>
                                    )}
                                    
                                    {claim.bankAccountName && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-24 flex-shrink-0 font-medium">Name:</span>
                                        <span className="text-sm font-semibold text-gray-900">{claim.bankAccountName}</span>
                                      </div>
                                    )}
                                    
                                    {claim.bankName && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-24 flex-shrink-0 font-medium">Bank:</span>
                                        <span className="text-sm font-semibold text-gray-900">{claim.bankName}</span>
                                      </div>
                                    )}
                                    
                                    {claim.branch && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-24 flex-shrink-0 font-medium">Branch:</span>
                                        <span className="text-sm font-semibold text-gray-900">{getBranchName(claim.branch)}</span>
                                      </div>
                                    )}
                                    
                                    {claim.accountNumber && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-24 flex-shrink-0 font-medium">Account #:</span>
                                        <span className="text-sm font-mono font-bold text-gray-900 tracking-wide">{claim.accountNumber}</span>
                                      </div>
                                    )}
                                    
                                    {claim.mobileMoneyProvider && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-24 flex-shrink-0 font-medium">Provider:</span>
                                        <span className="text-sm font-semibold text-gray-900">{claim.mobileMoneyProvider}</span>
                                      </div>
                                    )}
                                    
                                    {claim.mobileMoneyNumber && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-24 flex-shrink-0 font-medium">Mobile #:</span>
                                        <span className="text-sm font-mono font-bold text-gray-900 tracking-wide">{claim.mobileMoneyNumber}</span>
                                      </div>
                                    )}
                                    
                                    {/* SLAMS Request Number */}
                                    {claim.externalSubmissionsPhase5 && claim.externalSubmissionsPhase5.length > 0 && claim.externalSubmissionsPhase5[0].externalId && (
                                      <div className="flex items-start pt-2 border-t border-blue-200 mt-2">
                                        <span className="text-sm text-gray-600 w-24 flex-shrink-0 font-medium">SLAMS Ref:</span>
                                        <span className="text-sm font-mono font-bold text-blue-700 tracking-wide">{claim.externalSubmissionsPhase5[0].externalId}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Contact Details Column */}
                                {(claim.claimantMobile || claim.claimantEmail || claim.claimantAddress) && (
                                  <div className="space-y-2">
                                    <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center border-b border-blue-200 pb-1">
                                      <svg className="w-4 h-4 mr-1.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                      </svg>
                                      Contact Information
                                    </p>
                                    
                                    {claim.claimantMobile && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-20 flex-shrink-0 font-medium">Phone:</span>
                                        <a href={`tel:${claim.claimantMobile}`} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                                          {claim.claimantMobile}
                                        </a>
                                      </div>
                                    )}
                                    
                                    {claim.claimantEmail && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-20 flex-shrink-0 font-medium">Email:</span>
                                        <a href={`mailto:${claim.claimantEmail}`} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline break-all">
                                          {claim.claimantEmail}
                                        </a>
                                      </div>
                                    )}
                                    
                                    {claim.claimantAddress && (
                                      <div className="flex items-start">
                                        <span className="text-sm text-gray-600 w-20 flex-shrink-0 font-medium">Address:</span>
                                        <span className="text-sm text-gray-900 leading-relaxed">{claim.claimantAddress}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(claim.approvedBenefitAmount || claim.benefitAmount)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {claim.policyNumber || 'N/A'}
                        </p>
                      </div>
                      
                      <div className="flex space-x-2">
                        {claim.status === 'APPROVED' && (
                          <OmButton
                            type="button"
                            onClick={() => handleStatusUpdate(claim.id, 'PAYMENT_PENDING')}
                            disabled={processingPayments.has(claim.id)}
                            buttonType="filled-button"
                            size="sm"
                          >
                            {processingPayments.has(claim.id) ? (
                              <span className="flex items-center">
                                <OmCircularProgress
                                  indeterminate={true}
                                  size="small"
                                  className="mr-2"
                                />
                                Processing...
                              </span>
                            ) : (
                              'Process Payment'
                            )}
                          </OmButton>
                        )}
                        
                        {claim.status === 'PAYMENT_PENDING' && (
                          <OmButton
                            type="button"
                            onClick={() => handleAuthorizePayment(claim.id)}
                            disabled={processingPayments.has(claim.id)}
                            buttonType="filled-button"
                            size="sm"
                          >
                            {processingPayments.has(claim.id) ? (
                              <span className="flex items-center">
                                <OmCircularProgress
                                  indeterminate={true}
                                  size="small"
                                  className="mr-2"
                                />
                                Authorizing Payment...
                              </span>
                            ) : (
                              'Authorize Payment'
                            )}
                          </OmButton>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardSection>
        
        {/* Pagination Component */}
        {filteredClaims.length > itemsPerPage && (
          <div className="mt-6">
            <OmPagination
              currentPage={currentPage}
              totalItems={filteredClaims.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              showResultsInfo={true}
            />
          </div>
        )}
      </QuickActionsSection>
    </DashboardLayout>
  );
};

export default PaymentManagement;